/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { skillsRestoreVersion } from "../funcs/skillsRestoreVersion.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RecordSkillResult } from "../models/components/recordskillresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RestoreSkillVersionRequest,
  RestoreSkillVersionSecurity,
} from "../models/operations/restoreskillversion.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RestoreSkillVersionMutationVariables = {
  request: RestoreSkillVersionRequest;
  security?: RestoreSkillVersionSecurity | undefined;
  options?: RequestOptions;
};

export type RestoreSkillVersionMutationData = RecordSkillResult;

export type RestoreSkillVersionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * restoreVersion skills
 *
 * @remarks
 * Restore a historical valid version as the skill's current version without changing the immutable version record or explicit distribution pins.
 */
export function useRestoreSkillVersionMutation(
  options?: MutationHookOptions<
    RestoreSkillVersionMutationData,
    RestoreSkillVersionMutationError,
    RestoreSkillVersionMutationVariables
  >,
): UseMutationResult<
  RestoreSkillVersionMutationData,
  RestoreSkillVersionMutationError,
  RestoreSkillVersionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRestoreSkillVersionMutation(client, options),
    ...options,
  });
}

export function mutationKeyRestoreSkillVersion(): MutationKey {
  return ["@gram/client", "skills", "restoreVersion"];
}

export function buildRestoreSkillVersionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RestoreSkillVersionMutationVariables,
  ) => Promise<RestoreSkillVersionMutationData>;
} {
  return {
    mutationKey: mutationKeyRestoreSkillVersion(),
    mutationFn: function restoreSkillVersionMutationFn({
      request,
      security,
      options,
    }): Promise<RestoreSkillVersionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(skillsRestoreVersion(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
